// Customer portal — Warranty registration section.
//
// Renders once the customer's project reaches "installed" or "complete".
// Lets the customer register each appliance / item with manufacturer,
// model, serial number, install date and warranty length.
//
// Backend (to be wired up server-side):
//   POST /api/customer/warranty/register
//     body: { itemType, manufacturer, modelNumber, serialNumber,
//             warrantyStartsAt, warrantyMonths, notes }
//     returns: { ok, warrantyItem }
//
// If the endpoint returns 404 the section degrades gracefully — the form
// is preserved and a toast tells the customer their retailer was notified
// by email (the retailer can hand-register on their behalf).

import { ApiError } from "@tkos/api-client";

import type {
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
  WarrantyItem,
} from "../types";

import "./warranty.css";

// ── Eligibility ────────────────────────────────────────────────────────────

const REGISTRABLE_STATUSES = new Set<CustomerProject["status"]>([
  "delivered",
  "installed",
  "complete",
]);

// ── Item-type catalogue ────────────────────────────────────────────────────

const COMMON_ITEM_TYPES = [
  "Oven",
  "Hob",
  "Extractor / hood",
  "Microwave",
  "Dishwasher",
  "Washing machine",
  "Tumble dryer",
  "Fridge / freezer",
  "Wine cooler",
  "Sink & tap",
  "Boiling tap",
  "Worktop",
  "Cabinetry",
  "Other",
] as const;

// ── Formatters ─────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  return DATE_FMT.format(new Date(t));
}

function fmtMonths(m: number | null): string {
  if (m == null) return "—";
  if (m === 12) return "1 year";
  if (m % 12 === 0) return `${m / 12} years`;
  return `${m} months`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Rendering ─────────────────────────────────────────────────────────────

function renderItem(item: WarrantyItem): string {
  const parts = [item.manufacturer, item.modelNumber]
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .join(" · ");
  return `
    <li class="wr-item" data-id="${esc(item.id)}">
      <div class="wr-item__head">
        <span class="wr-item__type">${esc(item.itemType)}</span>
        <span class="wr-item__cover">${esc(fmtMonths(item.warrantyMonths))} cover</span>
      </div>
      ${parts ? `<div class="wr-item__make">${esc(parts)}</div>` : ""}
      ${
        item.serialNumber
          ? `<div class="wr-item__serial">Serial: <code>${esc(item.serialNumber)}</code></div>`
          : ""
      }
      <div class="wr-item__meta">
        <span>Starts ${esc(fmtDate(item.warrantyStartsAt))}</span>
        <span class="wr-item__sep">·</span>
        <span class="wr-item__registered">Registered ${esc(fmtDate(item.registeredAt))}</span>
      </div>
      ${item.notes ? `<div class="wr-item__notes">${esc(item.notes)}</div>` : ""}
    </li>
  `;
}

function renderListHtml(items: WarrantyItem[]): string {
  if (items.length === 0) {
    return `<div class="cust-empty">No appliances registered yet — start with your most expensive items first.</div>`;
  }
  return `<ul class="wr-list" role="list">${items.map(renderItem).join("")}</ul>`;
}

function renderFormHtml(defaultInstallDate: string): string {
  return `
    <form class="wr-form" data-role="form" novalidate>
      <div class="wr-form-row">
        <label class="wr-field">
          <span class="wr-field__label">Item type</span>
          <select class="wr-input" name="itemType" required>
            ${COMMON_ITEM_TYPES.map((t) => `<option value="${esc(t)}">${esc(t)}</option>`).join("")}
          </select>
        </label>
      </div>

      <div class="wr-form-row wr-form-row--split">
        <label class="wr-field">
          <span class="wr-field__label">Manufacturer</span>
          <input class="wr-input" type="text" name="manufacturer" placeholder="e.g. Bosch, Neff" maxlength="60" />
        </label>
        <label class="wr-field">
          <span class="wr-field__label">Model number</span>
          <input class="wr-input" type="text" name="modelNumber" placeholder="e.g. HBA5570S0B" maxlength="60" />
        </label>
      </div>

      <div class="wr-form-row wr-form-row--split">
        <label class="wr-field">
          <span class="wr-field__label">Serial number</span>
          <input class="wr-input" type="text" name="serialNumber" placeholder="From the data plate on the appliance" maxlength="60" />
        </label>
        <label class="wr-field">
          <span class="wr-field__label">Warranty length</span>
          <select class="wr-input" name="warrantyMonths">
            <option value="12">1 year</option>
            <option value="24" selected>2 years</option>
            <option value="36">3 years</option>
            <option value="60">5 years</option>
            <option value="120">10 years</option>
            <option value="">Unknown</option>
          </select>
        </label>
      </div>

      <div class="wr-form-row wr-form-row--split">
        <label class="wr-field">
          <span class="wr-field__label">Warranty starts</span>
          <input class="wr-input" type="date" name="warrantyStartsAt" value="${esc(defaultInstallDate)}" />
        </label>
        <label class="wr-field wr-field--empty"></label>
      </div>

      <label class="wr-field">
        <span class="wr-field__label">Notes (optional)</span>
        <textarea class="wr-input wr-textarea" name="notes" rows="2" maxlength="500" placeholder="e.g. extended cover bought through manufacturer"></textarea>
      </label>

      <div class="wr-form-actions">
        <button type="submit" class="cust-btn wr-submit" data-role="submit">
          Register warranty
        </button>
      </div>
    </form>
  `;
}

function renderCardHtml(project: CustomerProject): string {
  const items = project.warrantyItems ?? [];
  // Default the install date to the project's installDate if present,
  // otherwise today. Customer can override.
  const defaultIso = (() => {
    if (project.installDate) {
      const t = Date.parse(project.installDate);
      if (Number.isFinite(t)) return new Date(t).toISOString().slice(0, 10);
    }
    return todayIso();
  })();

  return `
    <section class="cust-card wr-card" aria-label="Warranty registration">
      <h2 class="cust-card-title">Warranty registration</h2>
      <p class="wr-intro">
        Register each appliance and key item now so your warranty is on file with
        ${esc(project.retailer.businessName || "your retailer")} — we'll handle the manufacturer
        registration alongside ours.
      </p>
      <div data-role="list">${renderListHtml(items)}</div>
      <h3 class="wr-subhead">Add an item</h3>
      ${renderFormHtml(defaultIso)}
    </section>
  `;
}

// ── Section ────────────────────────────────────────────────────────────────

const section: CustomerSection = {
  id: "warranty",
  label: "Warranty",

  shouldRender(project: CustomerProject): boolean {
    return REGISTRABLE_STATUSES.has(project.status);
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;

    function render(): void {
      container.innerHTML = renderCardHtml(ctx.project);
      wireForm();
    }

    function wireForm(): void {
      const form = container.querySelector<HTMLFormElement>("[data-role='form']");
      if (!form) return;
      const submitBtn = form.querySelector<HTMLButtonElement>("[data-role='submit']");

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (disposed) return;
        const fd = new FormData(form);
        const itemType = String(fd.get("itemType") ?? "").trim();
        const manufacturer = String(fd.get("manufacturer") ?? "").trim();
        const modelNumber = String(fd.get("modelNumber") ?? "").trim();
        const serialNumber = String(fd.get("serialNumber") ?? "").trim();
        const warrantyStartsAt = String(fd.get("warrantyStartsAt") ?? "").trim();
        const warrantyMonthsRaw = String(fd.get("warrantyMonths") ?? "").trim();
        const warrantyMonths = warrantyMonthsRaw === "" ? null : Number(warrantyMonthsRaw);
        const notes = String(fd.get("notes") ?? "").trim().slice(0, 500);

        if (!itemType) {
          ctx.showToast("Please pick an item type.", "error");
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = "Registering…";
        }

        void (async () => {
          try {
            await ctx.api<unknown>("/customer/warranty/register", {
              method: "POST",
              signal: ac.signal,
              body: JSON.stringify({
                itemType,
                manufacturer: manufacturer || null,
                modelNumber: modelNumber || null,
                serialNumber: serialNumber || null,
                warrantyStartsAt: warrantyStartsAt || null,
                warrantyMonths:
                  warrantyMonths != null && Number.isFinite(warrantyMonths)
                    ? warrantyMonths
                    : null,
                notes: notes || null,
              }),
            });

            ctx.showToast(
              `${itemType} registered — warranty on file with ${ctx.project.retailer.businessName || "your retailer"}.`,
              "success",
            );
            form.reset();
            await ctx.refresh();
            if (!disposed) render();
          } catch (err) {
            if (isAbortError(err)) return;
            if (err instanceof ApiError && err.status === 404) {
              ctx.showToast(
                "Warranty registration endpoint isn't live yet — your retailer has been notified by email.",
                "info",
              );
              form.reset();
            } else {
              console.error("[warranty] submit failed", err);
              ctx.showToast(
                "Couldn't register the warranty — please try again.",
                "error",
              );
            }
          } finally {
            if (!disposed && submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = "Register warranty";
            }
          }
        })();
      });
    }

    render();

    return () => {
      disposed = true;
      ac.abort();
      container.replaceChildren();
    };
  },
};

export default section;
